import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { ludsBaseFont, ludsHeadingXS, ludsTypography } from '../../shared/styles/typography';
import {
  CheckedEvent,
  GroupCheckedEvent,
  RadioButtonEvents,
  RadioButtonGroupEvents,
} from '../../shared/types/radiobutton.model';
import '../radiobutton/radiobutton';
import { RadioButtonOption } from '../radiobutton/radiobutton.type';

@customElement('luds-radio-button-group')
export class RadioButtonGroup extends LitElement {
  /**
   * List of checkbox options
   */
  @property({ type: Array }) options: Array<RadioButtonOption> = [];

  /**
   * Group label
   */
  @property({ type: String }) label = '';

  /**
   * Use HTML Group label
   */
  @property({ type: Boolean }) useHtmlLabel = false;

  /**
   * Selected value
   * If value is passed, it will be selected by default and override any checked value passed in options
   */
  @property({ type: String }) selectedValue: string = null;

  /**
   * 2 variants of radio button groups:
   * - default: vertical layout
   * - inline: horizontal layout
   */
  @property({ type: String }) layout: 'default' | 'inline' = 'default';

  static override styles = [
    overrideDesignTokens,
    ludsTypography,
    ludsHeadingXS,
    ludsBaseFont,
    css`
      :host {
        display: block;
      }

      .radio-group {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-spacing-12);
      }

      .radio-group-inline {
        display: flex;
        align-items: center;
        gap: var(--spacing-spacing-08);
        flex-wrap: wrap;
      }

      .question-label {
        overflow-wrap: break-word;
        word-break: break-word;
      }

      .question-label > pre {
        white-space: normal;
      }
    `,
  ];

  private handleChecked(event: CustomEvent<CheckedEvent>) {
    this.selectedValue = event.detail.value;

    // Update radiobuttons
    this.options = this.options.map((option) => ({
      ...option,
      indeterminate: false,
      checked: option.value === this.selectedValue,
    }));

    this.requestUpdate();
    this.dispatchEvent(
      new CustomEvent(RadioButtonGroupEvents.VALUE_CHANGED, {
        detail: { value: this.selectedValue },
        bubbles: true,
        composed: true,
      })
    );
  }

  override connectedCallback() {
    super.connectedCallback();
    this.addEventListener(RadioButtonEvents.CHECKED, this.handleChecked as EventListener);

    // Set selectedValue if any option is marked as checked
    const checkedOption = this.options.find((option) => option.checked);
    if (checkedOption) {
      this.selectedValue = checkedOption.value || null;
    }

    // Set selectedValue based on passed value
    if (this.selectedValue) {
      this.options = this.options.map((option) => ({
        ...option,
        checked: option.value === this.selectedValue,
      }));
    }
  }

  override disconnectedCallback() {
    this.removeEventListener(RadioButtonEvents.CHECKED, this.handleChecked as EventListener);
    super.disconnectedCallback();
  }

  override render() {
    return html`
      <div class=${this.layout === 'inline' ? 'radio-group-inline' : 'radio-group'}>
        ${this.label
          ? html`<label class="h-xs question-label">${this.useHtmlLabel ? unsafeHTML(this.label) : this.label}</label>`
          : null}
        ${this.options.map(
          (option) => html`
            <luds-radio-button
              .label="${option.label || ''}"
              .useHtmlLabel="${option.useHtmlLabel || false}"
              .value="${option.value || ''}"
              .variant="${option.variant || 'default'}"
              .checked="${option.checked || false}"
              .disabled="${option.disabled || false}"
              .indeterminate="${option.indeterminate || false}"
              .radioAriaLabel="${option.radioAriaLabel || ''}"
              .name="${option.name || ''}"
              .id="${option.id || ''}">
            </luds-radio-button>
          `
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-radio-button-group': RadioButtonGroup;
  }

  interface WindowEventMap {
    [RadioButtonGroupEvents.VALUE_CHANGED]: CustomEvent<GroupCheckedEvent>;
  }
}
